import {getLogger} from '@/shared/utils'
import {getFileS3Url} from '../core/_requests'
import {shareFileS3Url, type ShareRoot} from '../core/storage'
import {REST_SERVER, REST_ACTIONS} from '../components/external-viewer/handler-structures'
import {authedObserverFetch} from './observerApi'

export interface FileMetadata {
  objectInfo: {
    type: string
    id: string
    name: string
  }
  controllerInfo: {
    controllerClass: string
    controllerEndpoint: string
    objectClass: string
    viewerName: string
    viewerAppName?: string // Optional since not always provided by API
    viewerVersion?: string // e.g. semver — appended to S3 viewer path when present
    dataSource?: string // 'STORAGE' | 'CONTROLLER' — how the viewer gets its data (see ViewerDataSource)
    apiUrl?: string // ready endpoint path resolved by the OBserver, relative to REST_SERVER (CONTROLLER viewers)
    workbenchProperties: string[]
  }
  viewerURL: string
  mode: string
  apiUrl: string
}

export interface FileMetadataError {
  message: string
  code?: string
}



/** Format hints for a raw (non-.box) file, so the OBserver can resolve a client-side viewer. */
export interface RawFormatHint {
  /** Primary EDAM format CURIE from the file's ontology tags, e.g. "edam:format_1929". */
  formatTerm?: string
  /** Lambda format id fallback, e.g. "fasta". */
  formatId?: string
}

/**
 * Get production metadata from API.
 *
 * For a file in a folder someone else shares (`share`), `filename` is relative to that folder: the
 * request names the owner's key and the share's id, and the OBserver reads it with credentials for
 * that one folder (needs the OBserver's shared-folder support, omicsbox-bnd P1-E1).
 */
const getProductionMetadata = async (
  filename: string,
  formatHint?: RawFormatHint,
  share?: ShareRoot
): Promise<FileMetadata> => {
  const s3Url = share ? shareFileS3Url(share, filename) : await getFileS3Url(filename)
  const requestUrl = `${REST_SERVER}/${REST_ACTIONS.METADATA}`

  const response = await authedObserverFetch(requestUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      file: s3Url,
      // For raw (non-.box) files the OBserver resolves the viewer by format term without reading the
      // bytes; ignored for .box files (resolved by loading the object). Omitted keys are simply absent.
      ...(formatHint?.formatTerm ? {formatTerm: formatHint.formatTerm} : {}),
      ...(formatHint?.formatId ? {formatId: formatHint.formatId} : {}),
      ...(share ? {shareId: share.id} : {}),
    }),
  })

  const metadata = await response.json()
  return metadata
}

/**
 * Get file metadata based on environment
 */
export const getFileMetadata = async (
  filename: string,
  formatHint?: RawFormatHint,
  share?: ShareRoot
): Promise<FileMetadata> => {
  try {
    // if (process.env.NODE_ENV === 'development' && REST_SERVER?.includes('localhost:3002')) {

    // }

    return await getProductionMetadata(filename, formatHint, share)
  } catch (error) {
    getLogger().error('Failed to get file metadata:', error)
    throw error
  }
}
